// ============================================================================
// AutoSelectionForm.tsx — форма выбора (как в 1С).
// Рендерит ПРИВЯЗАННУЮ кастомную форму выбора (formType='selection'), если она
// задана; иначе — автоформу (динамический список + стандартные команды).
// Клик по строке — выбор значения; «Создать» — новая запись (с предзаполненным
// владельцем из связей параметров выбора).
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { AutoListForm } from './AutoListForm';
import { AutoObjectForm } from './AutoObjectForm';
import { CustomListFormRenderer } from './CustomListFormRenderer';
import { formBindingService } from '@/lib/formBindingService';
import { getSDK } from '@/store/sdkStore';
import type { ListFilter } from '@/types/formProperties';

interface AutoSelectionFormProps {
  objectType: string;
  objectName: string;
  value?: string;
  onSelect: (id: string, name: string) => void;
  /** Режим выбора (как в 1С): '' (любое) | 'items' (элементы) | 'groups' (группы) | 'both' (любое) */
  selectionMode?: string;
  /** Отборы формы выбора (как в 1С): параметры выбора + связи параметров выбора,
      применённые к динамическому списку */
  filters?: ListFilter[];
  /** Данные для СОЗДАНИЯ через форму выбора (как в 1С): предзаполненный владелец
      из связи параметров выбора («Владелец» ← реквизит формы/строки ТЧ) */
  createInitialData?: Record<string, unknown>;
  /** Колонки формы выбора (whitelist): например у валюты — только код/название/
      символ (без дат и служебных реквизитов). Пусто = авто-набор списка. */
  visibleKeys?: string[];
}

export function AutoSelectionForm({ objectType, objectName, value, onSelect, selectionMode = '', filters, createInitialData, visibleKeys }: AutoSelectionFormProps) {
  const [creating, setCreating] = useState(false);
  // Привязанная кастомная форма выбора (как в 1С: форма выбора может быть переопределена)
  const [selectionBinding, setSelectionBinding] = useState<{ elements: any[]; formAttrs: any[] } | null>(null);
  const [bindingChecked, setBindingChecked] = useState(false);

  const mode = selectionMode || 'both';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const binding = await formBindingService.findBinding(objectType, objectName, 'selection');
        if (cancelled) return;
        if (!binding) {
          setBindingChecked(true);
          return;
        }
        let layout: any = {};
        try {
          layout = typeof binding.layout === 'string' ? JSON.parse(binding.layout) : (binding.layout || {});
        } catch { layout = {}; }
        const props = await getSDK().formProperties.getFormProperties({ formId: binding.formId }).catch(() => ({ properties: [] as any[] }));
        const attrs = ((props as any).properties || []).map((p: any) => ({
          name: p.name,
          isMain: !!p.isMain,
          source: p.source as any,
        }));
        if (!cancelled) {
          setSelectionBinding({ elements: (layout.elements || []), formAttrs: attrs });
          setBindingChecked(true);
        }
      } catch {
        if (!cancelled) setBindingChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [objectType, objectName]);

  const handleRowClick = useCallback((row: Record<string, unknown>) => {
    const id = String(row['id'] ?? '');
    if (!id) return;
    const isGroup = row['is_group'] === true || row['is_group'] === '1' || row['is_group'] === 'true';
    // Режим выбора: элементы — группы не выбираются; группы — выбираются только группы
    if (mode === 'items' && isGroup) return;
    if (mode === 'groups' && !isGroup) return;
    // Отображаемое имя: name → value → code. ВАЖНО: `||`, НЕ `??` — у легаси-записей
    // name='' (пустая строка, не null), и `??` вернул бы '' (в поле пусто)
    const name = String(row['name'] || row['value'] || row['code'] || '');
    onSelect(id, name);
  }, [onSelect, mode]);

  const handleCreated = useCallback((id: string, name: string) => {
    setCreating(false);
    onSelect(id, name);
  }, [onSelect]);

  return (
    <div className="flex flex-col flex-1 min-h-0 p-2">
      {selectionBinding ? (
        <CustomListFormRenderer
          elements={selectionBinding.elements}
          objectType={objectType}
          objectName={objectName}
          formAttrs={selectionBinding.formAttrs}
          extraFilters={filters}
          onSelect={handleRowClick}
          onCreate={() => setCreating(true)}
        />
      ) : bindingChecked ? (
        <AutoListForm
          objectType={objectType}
          objectName={objectName}
          onSelect={handleRowClick}
          onCreate={() => setCreating(true)}
          showToolbar
          selectionMode={mode}
          filters={filters}
          visibleKeys={visibleKeys}
        />
      ) : (
        <div className="flex h-16 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
      {creating && (
        // AutoObjectForm сам рендерит своё окно (UniversalWindow) — внешняя обёртка
        // давала двойную модалку (два заголовка и два крестика).
        <AutoObjectForm
          objectType={objectType}
          objectName={objectName}
          onClose={() => setCreating(false)}
          onCreated={handleCreated}
          initialData={createInitialData}
        />
      )}
      {value && <span className="text-[10px] text-muted-foreground px-1 py-1">Выбрано: {value.slice(0, 8)}…</span>}
    </div>
  );
}
